/**
 * DispositionReasonModal — collects the REQUIRED reason when a recruiter parks a
 * lead at a terminal-negative disposition (Not Interested / Unreachable / Screen
 * Failed / Not Eligible). The reason rides the PATCH /contacts call as
 * { reason_code, disposition_note } and is recorded in the crm.contact_dispositions
 * ledger for the Dispositions report. A controlled reason code is required; the
 * note is optional. Cancelling leaves the lead's status unchanged.
 *
 * Screen Failed is also a STUDY-ATTRIBUTED outcome (the by-study screen-fail
 * numbers drive spend decisions), so with `askStudy` the modal additionally
 * requires the study the screen-fail belongs to; the caller sends it as
 * primary_study_id in the same PATCH (backend: 'study_confirmation_required').
 * In bulk mode (bulkCount) no per-lead pick is possible — the modal states that
 * each lead keeps its own considering study and the caller sends
 * study_confirmed:true.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import { dispositionReasonOptions, lifecycleMeta } from '../lib';
import { InterestCategory, PrescreenCriterion } from '../types';
import StudyPickField from './StudyPickField';
import { Z } from './ui';

interface Props {
  /** The terminal status being moved to (drives the reason list + title). */
  status: string;
  /** Require a study confirmation too (study-attributed statuses, single lead). */
  askStudy?: boolean;
  /** The lead's current considering study — preselects the study pick. */
  defaultStudyId?: string | null;
  /** Bulk path: number of selected leads (study pick becomes an acknowledgement). */
  bulkCount?: number;
  onConfirm: (reasonCode: string, note: string, studyId?: string, criterionId?: string, interestIds?: string[]) => void;
  onCancel: () => void;
}

export default function DispositionReasonModal({ status, askStudy = false, defaultStudyId, bulkCount, onConfirm, onCancel }: Props) {
  const options = dispositionReasonOptions(status);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [studyId, setStudyId] = useState(defaultStudyId || '');
  const label = lifecycleMeta(status).label;
  const bulk = typeof bulkCount === 'number' && bulkCount > 0;
  const needsStudyPick = askStudy && !bulk;

  // OPTIONAL failed-question picker (owner 2026-08-07). A dropdown "Not
  // eligible" now writes a real disqualification, and naming the criterion is
  // what puts it in the DQ report's by-criterion breakdown — the one that shows
  // a screener question eating the funnel. Optional on purpose: recruiters use
  // the dropdown BECAUSE it is fast, and a required picker would push them back
  // to leaving no reason at all.
  //
  // Single-lead not_eligible only: a bulk batch has no one study, and the other
  // dispositions aren't disqualifications. Silent on every failure — no
  // prescreen published, a retired study, a dead network call — because this
  // field is a bonus, never a blocker.
  const [criterionId, setCriterionId] = useState('');
  const [criteria, setCriteria] = useState<PrescreenCriterion[]>([]);
  const wantsCriteria = status === 'not_eligible' && !bulk && Boolean(defaultStudyId);

  // OPTIONAL future-study lists (owner 2026-08-20). The close-out moment IS
  // when a recruiter knows "not a fit for this study, right profile for one we
  // regularly get" — so the modal offers the catalog right here. Same posture
  // as the criterion picker: single-lead only, silent on every fetch failure,
  // a bonus never a blocker. Each picked list becomes a crm.contact_interests
  // row after the save (PATCH body interest_category_ids).
  const [interestIds, setInterestIds] = useState<string[]>([]);
  const [interestOptions, setInterestOptions] = useState<InterestCategory[]>([]);
  const wantsInterests = !bulk;

  useEffect(() => {
    if (!wantsInterests) { setInterestOptions([]); return; }
    let alive = true;
    api.get('/api/crm/interest-categories?active=true')
      .then((res) => { if (alive) setInterestOptions(res.data?.categories || []); })
      .catch(() => { if (alive) setInterestOptions([]); });
    return () => { alive = false; };
  }, [wantsInterests]);

  const toggleInterest = (id: string) =>
    setInterestIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  useEffect(() => {
    if (!wantsCriteria) { setCriteria([]); return; }
    let alive = true;
    api.get(`/api/crm/studies/${defaultStudyId}/prescreen`)
      .then((res) => {
        if (!alive) return;
        // Calculated rows are derived, never "the question they failed".
        setCriteria((res.data?.prescreen?.criteria || []).filter((c: PrescreenCriterion) => !c.calculation));
      })
      .catch(() => { if (alive) setCriteria([]); });
    return () => { alive = false; };
  }, [wantsCriteria, defaultStudyId]);

  // The category follows the chosen question, and stays hand-editable — the
  // DisqualifyModal behaviour, so the two paths feel like one tool.
  useEffect(() => {
    if (!criterionId) return;
    const c = criteria.find((x) => x.id === criterionId);
    if (c?.category) setReasonCode(c.category);
  }, [criterionId, criteria]);

  // Escape cancels; the dialog traps focus on the select for fast keyboard use.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const confirm = () => {
    if (!reasonCode) return;
    if (needsStudyPick && !studyId) return;
    onConfirm(
      reasonCode,
      note.trim(),
      needsStudyPick ? studyId : undefined,
      criterionId || undefined,
      interestIds.length ? interestIds : undefined,
    );
  };

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`}
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Reason for ${label}`}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Mark as “{label}”
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Pick the reason this lead is being closed out — it feeds the recruiter
          disposition report.
        </p>

        {criteria.length > 0 && (
          <>
            <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Which question did they fail?{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <select
              value={criterionId}
              onChange={(e) => setCriterionId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="">Skip — no specific question</option>
              {criteria.map((c) => (
                <option key={c.id} value={c.id}>
                  [{c.criterion_kind === 'exclusion' ? 'EXCL' : 'INCL'}] {c.display_question.slice(0, 90)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
              Naming it puts this lead in the by-question breakdown of the “why
              aren’t leads qualifying” report. Skipping still records everything
              else.
            </p>
          </>
        )}

        <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Reason <span className="text-rose-500">*</span>
        </label>
        <select
          autoFocus
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          <option value="" disabled>Select a reason…</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {needsStudyPick && (
          <StudyPickField value={studyId} onChange={setStudyId} />
        )}
        {askStudy && bulk && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            Each of the {bulkCount} leads is recorded under its own considering
            study. If any pivoted to a different study, verify those leads
            individually so the by-study reports stay accurate.
          </p>
        )}

        <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Note <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 1000))}
          rows={3}
          placeholder="Any specifics worth keeping…"
          className="mt-1 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />

        {interestOptions.length > 0 && (
          <>
            <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Keep for a future study{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {interestOptions.map((c) => {
                const on = interestIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleInterest(c.id)}
                    aria-pressed={on}
                    className={`inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full border px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-1 ${
                      on
                        ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
              Not a fit today, right profile for one of these? They'll be on that
              list when we get a study like it.
            </p>
          </>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!reasonCode || (needsStudyPick && !studyId)}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save status
          </button>
        </div>
      </div>
    </div>
  );
}
